import { registerPlugin, WebPlugin } from '@capacitor/core';

import { appendAppLog } from './appLog';
import { desktopBruecke, istDesktop } from './gleis';

/**
 * Das iPad wach halten, solange eine Station läuft.
 *
 * **Warum.** iOS sperrt den Bildschirm nach seiner eigenen Frist — werkseitig nach zwei
 * Minuten. Genau dann rührt der Prüfer das Gerät nicht an: er sieht dem Prüfling zu. Danach
 * steht er vor dem Sperrbildschirm, während die Untersuchung vor ihm endet. Marktübliche
 * Prüfungs-Apps schalten die Frist deshalb ab (`docs/oberflaeche-luecken.md`, Punkt 1).
 *
 * ⚠️ **Das ist nicht die Schlafscheibe** (`features/schlaf/`). Die dunkelt nach fünf Minuten
 * Ruhe unsere eigene Oberfläche ab und ist mit einem Tipp wieder weg; sie hält iOS nicht auf.
 * Zusammen ergibt beides das Richtige: der Schirm wird dunkel, das Gerät sperrt nicht, und ein
 * Fingertipp bringt den Bogen zurück.
 *
 * ⚠️ **Es muss wieder abgeschaltet werden.** Das Flag gilt, solange die App vorn ist — und
 * vorn bleibt sie den ganzen Prüfungstag. Wer es setzt und nie zurücknimmt, lässt das iPad
 * auch in der Mittagspause leuchten; auf einem iPad 6 ist das ein spürbarer Teil der Ladung.
 * Deshalb hängt es an der laufenden Station, nicht am Start der App.
 */

export interface WachPlugin {
  setze(options: { an: boolean }): Promise<{ an: boolean }>;
  stand(): Promise<{ an: boolean }>;
}

/**
 * Im Browser gibt es keine Bildschirmsperre, die uns anginge.
 *
 * ⚠️ **Bewusst ohne `navigator.wakeLock`.** Die Screen Wake Lock API gibt es im WKWebView von
 * iOS 15 nicht (`memory/ios15-constraints.md`), und ein zweiter Weg, der nur im
 * Entwicklungsbrowser greift, verdeckte am Gerät genau den Fall, den er beheben soll.
 *
 * ⭐ **Der Desktop ist kein Browser** — dort hält `powerSaveBlocker` den Schirm wach
 * (`WachDesktop`), und der Bildschirmschoner eines Windows-Rechners ist derselbe Störfall wie
 * die Sperre eines iPads: der Prüfer sieht dem Prüfling zu und rührt nichts an.
 */
class WachWeb extends WebPlugin implements WachPlugin {
  private an = false;
  async setze(options: { an: boolean }): Promise<{ an: boolean }> {
    this.an = options.an;
    return { an: this.an };
  }
  async stand(): Promise<{ an: boolean }> {
    return { an: this.an };
  }
}

/**
 * Am Desktop hält `powerSaveBlocker` den Schirm wach — im Hauptprozess, s.
 * `desktop/main.cts::richteWachEin`.
 *
 * ⚠️ **`stand()` fragt nicht nach.** Der Hauptprozess führt genau eine Sperre; was er
 * zurückgibt, ist dasselbe, was `setze()` gemeldet hat. Ein zweiter Weg dorthin wäre eine
 * Tür mehr für eine Auskunft, die schon dasteht.
 */
class WachDesktop extends WebPlugin implements WachPlugin {
  private an = false;

  async setze(options: { an: boolean }): Promise<{ an: boolean }> {
    const bruecke = desktopBruecke();
    if (!bruecke) throw new Error('Kein Desktop-Gleis — die Brücke fehlt.');
    const ergebnis = await bruecke.wach(options.an);
    this.an = ergebnis.an;
    return ergebnis;
  }

  async stand(): Promise<{ an: boolean }> {
    return { an: this.an };
  }
}

export const Wach = registerPlugin<WachPlugin>('Wach', {
  web: () => (istDesktop() ? new WachDesktop() : new WachWeb()),
});

/**
 * Setzt den Stand und schreibt jeden **Wechsel** ins Dateilog.
 *
 * Nicht jeden Aufruf: der Web-Teil ruft bei jeder Zustandsänderung der Oberfläche, das wären
 * hunderte Zeilen am Tag. Der Wechsel selbst ist die Auskunft — bleibt ein iPad mitten in
 * einer Prüfung stehen, ist die erste Frage, ob es eingeschlafen ist.
 *
 * ⚠️ **Wirft nie.** Ein Gerät, das die Bildschirmsperre nicht abschalten kann, ist immer noch
 * ein Gerät, auf dem geprüft wird.
 */
let zuletzt: boolean | null = null;

export async function halteWach(an: boolean): Promise<void> {
  if (zuletzt === an) return;
  zuletzt = an;
  try {
    await Wach.setze({ an });
    await appendAppLog(an ? 'Bildschirmsperre aus (Station läuft).' : 'Bildschirmsperre wieder an.');
  } catch (fehler) {
    await appendAppLog('Bildschirmsperre liess sich nicht umstellen: ' + String(fehler), 'warn');
  }
}

/** Nur für Tests: den gemerkten Stand vergessen. */
export function vergissWachstand(): void {
  zuletzt = null;
}
